import React from 'react';
import * as Yup from 'yup';
import {
  ApplicationAction,
  ApplicationActionType,
  Button,
  InputField,
  Modal,
  stripWhitespace,
} from 'src/features/common';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/features/common/components/button/revamp-button';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { MiniAppFocusWrapper } from 'src/components';
import { serialNumberMask, serialNumberPattern } from 'src/helpers/formatting';
import { useApplication } from 'src/hooks/useApplication';
import { getProduct } from 'src/features/device-activation/helpers/utils';
import { CardMachine } from 'src/features/common/types/application/card-machine';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';
import { isInCartAndCanBeCollected } from 'src/features/dpo/pages/assign-devices/helpers';
import { Address } from '../../pages/assign-devices/assign-devices';
import { DeliveryOptions } from '@vfs-digital-channels/ns-utils';
import { Collection } from 'src/features/common/types/application/collection';

interface Props {
  visible: boolean;
  onClose: () => void;
  onVisible?: () => void;
  onContinue?: (isDuplicate: boolean) => void;
  addresses?: Address[];
}

interface InitValues {
  serialNumber: string;
  confirmSerial: string;
}

export function EnterSerialModal(props: Props) {
  const { application, dispatch, catalogue } = useApplication();
  const initialValues: InitValues = {
    serialNumber: '',
    confirmSerial: '',
  };

  const submit = (values: InitValues, helpers: FormikHelpers<InitValues>) => {
    const product = getProduct(catalogue.products, values.serialNumber.toUpperCase());
    const isDuplicate = application.cardMachines?.find((cardMachine) =>
      cardMachine.barcode.toLowerCase().includes(values.serialNumber.toLowerCase()),
    );
    const cardMachineAddresses = application.cardMachines?.map(
      (cardMachine) => cardMachine.addressId,
    );

    const collectionAddresses = (props?.addresses || []).filter(
      (address) =>
        address.deliveryOption === DeliveryOptions.Stock && address.productId === product.device.id,
    );

    const matchingCollectionAddresses = collectionAddresses.filter(
      (address) =>
        !cardMachineAddresses?.includes(address.addressId) || address.productDetails.quantity > 1,
    );

    const actions: ApplicationAction[] = isDuplicate
      ? []
      : [
          {
            type: ApplicationActionType.UPDATE_CARD_MACHINE,
            cardMachines: [
              ...(application.cardMachines || []),
              {
                barcode: values.serialNumber.toUpperCase(),
                name: '',
                productId: product.device.id,
                acquiringChannel: product.device.acquiringChannel,
                addressId:
                  collectionAddresses.length > 1
                    ? matchingCollectionAddresses[0].addressId
                    : collectionAddresses[0].addressId,
              },
            ],
          },
        ];

    dispatch({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions,
    });
    if (props.onContinue) {
      props.onContinue(!!isDuplicate);
    }
    helpers.resetForm();
  };

  React.useEffect(() => {
    if (props.visible) {
      props.onVisible && props.onVisible();
    }
  }, [props.visible]);

  return (
    <Modal onClose={props.onClose} visible={props.visible} size="small">
      <Formik
        initialValues={initialValues}
        validateOnBlur
        validateOnChange
        onSubmit={submit}
        validateOnMount
        validationSchema={Yup.object().shape({
          serialNumber: Yup.string()
            .required('Please enter serial number')
            .transform((originalValue) =>
              originalValue ? stripWhitespace(originalValue.toUpperCase()) : '',
            )
            .matches(serialNumberPattern, 'Invalid serial number format')
            .test({
              name: 'startsWithMachine',
              test: (value) => {
                const prefix = value.includes('N5')
                  ? value.slice(0, 2).toUpperCase()
                  : value.slice(0, 3).toUpperCase(); // Extract the first two/three characters and convert to uppercase
                let filteredDevices: CardMachine[] = [];
                let deviceName: string = '';
                switch (prefix) {
                  case 'K3R':
                    // Filter devices that start with 'K3R'
                    filteredDevices = (application.cardMachines || []).filter((machine) =>
                      machine.barcode.toUpperCase().startsWith('K3R'),
                    );
                    deviceName = DEVICE_NAMES.VODAPAY_KWIKA;
                    break;
                  case 'N86':
                    // Filter devices that start with 'N86'
                    filteredDevices = (application.cardMachines || []).filter((machine) =>
                      machine.barcode.toUpperCase().startsWith('N86'),
                    );
                    deviceName = DEVICE_NAMES.MAX_2;
                    break;
                  case 'N5':
                    // Filter devices that start with 'N5'
                    filteredDevices = (application.cardMachines || []).filter((machine) =>
                      machine.barcode.toUpperCase().startsWith('N5'),
                    );
                    deviceName = DEVICE_NAMES.VODAPAY_MAX;
                    break;
                  default:
                    // Invalid prefix
                    filteredDevices = [];
                }

                if (filteredDevices.length) {
                  const productId = filteredDevices[0].productId;

                  // get the quantity of all collections that match the product Id
                  const quantity = application.collections
                    ?.filter((collection: Collection) => collection.productId === productId)
                    .reduce((accumulator: number, collection: Collection) => {
                      if (collection.productId === productId) {
                        return accumulator + collection.quantity;
                      }

                      return accumulator;
                    }, 0);

                  if (quantity && filteredDevices.length < quantity) return true;

                  return false;
                }

                if (
                  !isInCartAndCanBeCollected(
                    deviceName,
                    application.cart || [],
                    application.collections || [],
                  )
                )
                  return false;

                return true;
              },
              message: ({ value }) => {
                const prefix = value.includes('N5')
                  ? value.slice(0, 2).toUpperCase()
                  : value.slice(0, 3).toUpperCase(); // Extract the first two/three characters and convert to uppercase
                let isCollectable: boolean = true;
                let scannedMessage: string = '';
                let noCollectionMessage: string = '';
                switch (prefix) {
                  case 'K3R':
                    isCollectable = isInCartAndCanBeCollected(
                      DEVICE_NAMES.VODAPAY_KWIKA,
                      application.cart || [],
                      application.collections || [],
                    );
                    scannedMessage = 'VodaPay Kwika has already been scanned';
                    noCollectionMessage = 'VodaPay Kwika is not marked as collect';
                    break;
                  case 'N86':
                    isCollectable = isInCartAndCanBeCollected(
                      DEVICE_NAMES.MAX_2,
                      application.cart || [],
                      application.collections || [],
                    );
                    scannedMessage = 'VodaPay MAX 2 has already been scanned';
                    noCollectionMessage = 'VodaPay MAX 2 is not marked as collect';
                    break;
                  case 'N5':
                    isCollectable = isInCartAndCanBeCollected(
                      DEVICE_NAMES.VODAPAY_MAX,
                      application.cart || [],
                      application.collections || [],
                    );
                    noCollectionMessage = 'VodaPay MAX is not marked as collect';
                    scannedMessage = 'VodaPay MAX has already been scanned';
                    break;
                  default:
                    scannedMessage = 'Invalid serial number';
                }

                return isCollectable ? scannedMessage : noCollectionMessage;
              },
            }),
          confirmSerial: Yup.string()
            .required('Please confirm your serial number')
            .transform((originalValue) =>
              originalValue ? stripWhitespace(originalValue.toUpperCase()) : '',
            )
            .oneOf([Yup.ref('serialNumber')], 'Serial number Fields must match'),
        })}
      >
        {({ isValid }) => (
          <Form>
            <section className="flex flex-col items-center gap-7">
              <h1 className="text-4xl font-medium">Enter barcode</h1>
              <h3 className="text-lg font-normal">Please enter the barcode below</h3>
              <section className="flex flex-col gap-5 md:min-w-[370px]">
                <Field
                  name={'serialNumber'}
                  type="text"
                  mask={serialNumberMask}
                  id={`serialNumber`}
                  placeholder="Enter barcode"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="masked"
                  className="w-4/5 uppercase"
                  automationid="DA_Enter_Barcode"
                />

                <Field
                  name={'confirmSerial'}
                  type="text"
                  id={`confirmSerial`}
                  mask={serialNumberMask}
                  placeholder="Confirm barcode"
                  component={MiniAppFocusWrapper}
                  innerComponent={InputField}
                  fieldType="masked"
                  className="w-4/5 uppercase"
                  automationid="DA_Confirm_Barcode"
                />
              </section>
              <section className="flex flex-col items-center w-4/5 gap-7">
                <Button
                  type={'submit'}
                  disabled={!isValid}
                  buttonStyle={DefaultButtonStyle()
                    .hasPrimaryAction()
                    .isMediumSize()
                    .hasLightMode()
                    .build()}
                  buttonParams={{
                    type: ButtonParamType.NormalButton,
                    onClick: () => {},
                  }}
                  className="w-56"
                  automationid={`DA_Continue_Button`}
                >
                  Continue
                </Button>
              </section>
            </section>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
